/**
 * Enhanced API Service
 * Secure authentication with HTTP-only cookies and automatic token refresh
 */

import axios from 'axios'
import logger from '../utils/logger'
import { sanitizeTelemetryUrl } from '@/privacy/telemetrySanitizer'
import { createAuthRefreshCoordinator } from '@x3algo/shared/browser/authRefreshCoordinator'
import { authAttempts, authAttemptError } from '@utils/authAttemptState'

// Create axios instance with default config
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  withCredentials: true, // Essential for HTTP-only cookies
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// CSRF: read XSRF-TOKEN cookie and attach as header on every mutating request
api.interceptors.request.use((config) => {
  const methods = ['post', 'put', 'patch', 'delete']
  if (methods.includes(config.method)) {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
    if (match) config.headers['X-XSRF-TOKEN'] = decodeURIComponent(match[1])
  }
  return config
})

/**
 * Get CSRF token from cookie
 */
const getCsrfToken = () => {
  const name = 'XSRF-TOKEN='
  const decodedCookie = decodeURIComponent(document.cookie)
  const cookies = decodedCookie.split(';')
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim()
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length)
    }
  }
  return null
}

const authCommandPaths = new Set([
  '/auth/signup',
  '/auth/login',
  '/auth/2fa/login-verify',
  '/auth/refresh-token',
  '/auth/logout',
  '/auth/2fa/setup',
  '/auth/2fa/verify',
  '/auth/2fa/disable',
  '/auth/2fa/cancel',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/user/password',
  '/user/change-password'
])

const requestPath = url =>
  String(url || '')
    .split('?')[0]
    .replace(/^\/api(?=\/)/, '')
const isAuthCommand = (url, method) => {
  const path = requestPath(url)
  if (path === '/user/sessions') return method?.toLowerCase() === 'delete'
  return authCommandPaths.has(path)
}

async function ensureCsrfToken(signal) {
  if (getCsrfToken()) return
  await api.get('/csrf-token', {
    signal,
    skipAuthRefresh: true,
    skipMockAuth: true
  })
  if (!getCsrfToken())
    throw new Error('Unable to initialize request protection.')
}

const refreshCoordinator = createAuthRefreshCoordinator({
  state: authAttempts,
  async requestRefresh(attempt, signal) {
    await ensureCsrfToken(signal)
    authAttempts.assertCurrent(attempt)
    try {
      return (
        await api.post(
          '/auth/refresh-token',
          {},
          {
            signal,
            authAttempt: attempt,
            skipAuthRefresh: true,
            skipMockAuth: true
          }
        )
      ).data
    } catch (error) {
      const status = error.response?.status || error.status
      const code = error.response?.data?.code || error.code
      if (status === 401 || code === 'PRODUCT_SESSION_REJECTED') {
        const reauthentication = new Error('Sign in again to continue.')
        reauthentication.code = 'AUTH_REAUTHENTICATION_REQUIRED'
        reauthentication.requiresReauthentication = true
        reauthentication.privateAuthFailure = true
        reauthentication.status = 401
        throw reauthentication
      }
      throw error
    }
  },
  async probeCurrentUser(attempt, signal) {
    return (
      await api.get('/user/me', {
        signal,
        authAttempt: attempt,
        skipAuthRefresh: true,
        skipMockAuth: true
      })
    ).data
  },
  onRefreshed(result, attempt) {
    if (!authAttempts.isCurrent(attempt)) return
    window.dispatchEvent(
      new CustomEvent('token:refreshed', {
        detail: {
          ...(result.tokenMetadata
            ? { tokenMetadata: result.tokenMetadata }
            : {}),
          ...(result.data?.authSession
            ? { authSession: result.data.authSession }
            : {})
        }
      })
    )
  }
})

// Request interceptor for debugging and auth
api.interceptors.request.use(
  (config) => {
    config.authAttempt = config.authAttempt || authAttempts.capture()
    if (isAuthCommand(config.url, config.method))
      authAttempts.assertCurrent(config.authAttempt)

    // Add CSRF token for non-GET requests
    if (
      config.method &&
      !['get', 'head', 'options'].includes(config.method.toLowerCase())
    ) {
      const csrfToken = getCsrfToken()
      if (csrfToken) {
        config.headers['X-XSRF-TOKEN'] = csrfToken
      }
    }

    // Add request ID for request-level correlation
    config.headers['X-Request-ID'] = generateRequestId()

    logger.apiRequest(config.method, sanitizeTelemetryUrl(config.url))

    return config
  },
  (error) => {
    if (error.privateAuthFailure) return Promise.reject(error)
    logger.apiError(
      'REQUEST',
      sanitizeTelemetryUrl(error.config?.url || 'unknown'),
      error
    )
    return Promise.reject(error)
  }
)

// Response interceptor for token refresh and error handling
api.interceptors.response.use(
  (response) => {
    logger.apiResponse(
      response.status,
      sanitizeTelemetryUrl(response.config.url)
    )
    return response
  },
  async (error) => {
    const originalRequest = error.config || {}

    if (error.privateAuthFailure || originalRequest.privateAuthRequest) {
      return Promise.reject(error)
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._productAuthRetried &&
      !originalRequest.skipAuthRefresh &&
      !isAuthCommand(originalRequest.url, originalRequest.method)
    ) {
      const attempt = originalRequest.authAttempt || authAttempts.capture()
      if (!authAttempts.isCurrent(attempt))
        return Promise.reject(authAttemptError())
      originalRequest._productAuthRetried = true
      try {
        await refreshAuthToken(attempt)
        authAttempts.assertCurrent(attempt)
        return api(originalRequest)
      } catch (refreshError) {
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors - extract proper error messages from API response
    if (error.response?.status === 403) {
      // Extract the actual error message from the API response
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Access denied. You do not have permission to perform this action.'
      logger.error('🚫 Access denied:', errorMessage)

      // Enhance the error object with the extracted message
      error.message = errorMessage
      error.userMessage = errorMessage

      showErrorNotification(errorMessage)
    } else if (error.response?.status === 429) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Too many requests. Please slow down.'
      logger.error('⏳ Rate limit exceeded:', errorMessage)
      error.message = errorMessage
      error.userMessage = errorMessage
      showErrorNotification(errorMessage)
    } else if (error.response?.status >= 400) {
      // Handle all other HTTP errors by extracting the message
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        `Request failed with status code ${error.response.status}`
      error.message = errorMessage
      error.userMessage = errorMessage
    } else if (!error.response) {
      logger.error('🌐 Network error')
      error.message = 'Network error. Please check your internet connection.'
      error.userMessage = error.message
    }

    return Promise.reject(error)
  }
)

/**
 * Handle authentication failure
 */
const handleAuthenticationFailure = () => {
  authAttempts.requireReauthentication()
  // Clear any client-side auth state
  localStorage.removeItem('user')
  localStorage.removeItem('mockToken')

  // Emit auth failure event for context to handle
  window.dispatchEvent(new CustomEvent('auth:logout'))

  // Redirect to login if not already there
  if (
    window.location.pathname !== '/login' &&
    window.location.pathname !== '/'
  ) {
    window.location.href =
      '/?redirect=' + encodeURIComponent(window.location.pathname)
  }
}

export const refreshAuthToken = async (attempt = authAttempts.capture()) => {
  try {
    return await refreshCoordinator.refresh(attempt)
  } catch (error) {
    if (
      error.requiresReauthentication ||
      error.code === 'AUTH_REAUTHENTICATION_REQUIRED'
    ) {
      handleAuthenticationFailure()
    }
    throw error
  }
}

/**
 * Show error notification
 */
const showErrorNotification = message => {
  // This would integrate with your notification system
  logger.error('Notification:', message)
  // You could dispatch a custom event or call a notification service
}

/**
 * Generate unique request ID for tracing
 */
const generateRequestId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

/**
 * Enhanced Authentication Service
 */
export const authService = {
  /**
   * Login user with email and password
   */
  async login(credentials, attempt = authAttempts.capture()) {
    try {
      await ensureCsrfToken()
      authAttempts.assertCurrent(attempt)
      const response = await api.post(
        '/auth/login',
        {
          email: credentials.email,
          password: credentials.password,
          rememberMe: credentials.rememberMe || false
        },
        { authAttempt: attempt, skipAuthRefresh: true, skipMockAuth: true }
      )

      if (response.data.success) {
        // Check if 2FA is required
        if (response.data.requiresTwoFactor) {
          logger.info('2FA required for login')
          return {
            success: true,
            requiresTwoFactor: true,
            tempToken: response.data.tempToken,
            authAttempt: attempt,
            message: response.data.message
          }
        }

        logger.info('Login successful')
        return {
          success: true,
          user: response.data.user,
          authSession: response.data.data?.authSession,
          sessionReplaced: response.data.sessionReplaced === true,
          message: response.data.message
        }
      }

      return {
        success: false,
        message: response.data.message || 'Login failed'
      }
    } catch (error) {
      // Extract error message from nested error object
      let message = 'Login failed'
      if (error.response?.data?.error?.message) {
        message = error.response.data.error.message
      } else if (error.response?.data?.message) {
        message = error.response.data.message
      } else if (error.message) {
        message = error.message
      }

      return {
        success: false,
        message,
        stale: !authAttempts.isCurrent(attempt)
      }
    }
  },

  /**
   * Register new user
   */
  async signup(userData, attempt = authAttempts.capture()) {
    try {
      await ensureCsrfToken()
      authAttempts.assertCurrent(attempt)
      const response = await api.post(
        '/auth/signup',
        {
          name: userData.name,
          email: userData.email,
          password: userData.password,
          confirmPassword: userData.confirmPassword
        },
        { authAttempt: attempt, skipAuthRefresh: true, skipMockAuth: true }
      )

      if (response.data.success) {
        logger.info('Signup successful')
        return {
          success: true,
          user: response.data.user,
          authSession: response.data.data?.authSession,
          message: response.data.message,
          enrollment: response.data.data?.enrollment
        }
      }

      return {
        success: false,
        message: response.data.message || 'Registration failed'
      }
    } catch (error) {
      // Extract error message from nested error object
      let message = 'Registration failed'
      if (error.response?.data?.error?.message) {
        message = error.response.data.error.message
      } else if (error.response?.data?.message) {
        message = error.response.data.message
      } else if (error.message) {
        message = error.message
      }

      return {
        success: false,
        message,
        stale: !authAttempts.isCurrent(attempt),
        suggestions:
          error.response?.data?.error?.suggestions ||
          error.response?.data?.suggestions
      }
    }
  },

  /**
   * Verify 2FA code during login
   */
  async verifyLogin2FA(
    tempToken,
    code,
    isBackupCode = false,
    attempt = authAttempts.capture()
  ) {
    try {
      await ensureCsrfToken()
      authAttempts.assertCurrent(attempt)
      const body = { tempToken }
      if (isBackupCode) {
        body.backupCode = code
      } else {
        body.totpCode = code
      }
      const response = await api.post('/auth/2fa/login-verify', body, {
        authAttempt: attempt,
        skipAuthRefresh: true,
        skipMockAuth: true
      })

      if (response.data.success) {
        logger.info('2FA login verification successful')
        return {
          success: true,
          user: response.data.user,
          authSession: response.data.data?.authSession,
          sessionReplaced: response.data.sessionReplaced === true,
          message: response.data.message
        }
      }

      return {
        success: false,
        message: response.data.message || '2FA verification failed'
      }
    } catch (error) {
      let message = '2FA verification failed'
      if (error.response?.data?.error?.message) {
        message = error.response.data.error.message
      } else if (error.response?.data?.message) {
        message = error.response.data.message
      }
      return {
        success: false,
        message,
        stale: !authAttempts.isCurrent(attempt)
      }
    }
  },

  /**
   * Logout user
   */
  async logout(attempt = authAttempts.capture()) {
    try {
      await ensureCsrfToken()
      authAttempts.assertCurrent(attempt)
      const response = await api.post(
        '/auth/logout',
        {},
        {
          authAttempt: attempt,
          skipAuthRefresh: true,
          skipMockAuth: true
        }
      )
      return {
        localSignedOut: true,
        serverRevoked: response.data?.data?.serverRevoked === true,
        alreadyRevoked: response.data?.data?.alreadyRevoked === true
      }
    } catch (error) {
      return { localSignedOut: true, serverRevoked: false }
    }
  },

  /**
   * Get current user data
   */
  async getCurrentUserSnapshot(
    attempt = authAttempts.capture(),
    { skipAuthRefresh = false } = {}
  ) {
    try {
      const response = await api.get('/user/me', {
        authAttempt: attempt,
        skipAuthRefresh
      })
      authAttempts.assertCurrent(attempt)

      if (response.data.success && response.data.user) {
        authAttempts.acceptCurrentSession(
          attempt,
          response.data.data?.authSession
        )
        authAttempts.assertCurrent(attempt)
        localStorage.setItem('user', JSON.stringify(response.data.user))
        return {
          user: response.data.user,
          authSession: response.data.data?.authSession
        }
      }

      return null
    } catch (error) {
      if (authAttempts.isCurrent(attempt) && error.response?.status === 401) {
        localStorage.removeItem('user')
      }
      throw error
    }
  },

  async getCurrentUser(attempt = authAttempts.capture()) {
    try {
      return (await this.getCurrentUserSnapshot(attempt))?.user || null
    } catch (_error) {
      return null
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(profileData, attempt = authAttempts.capture()) {
    try {
      const response = await api.put('/user/profile', profileData, {
        authAttempt: attempt
      })
      authAttempts.assertCurrent(attempt)

      if (response.data.success) {
        // Update stored user data
        localStorage.setItem('user', JSON.stringify(response.data.user))
        return {
          success: true,
          user: response.data.user,
          message: response.data.message
        }
      }

      return {
        success: false,
        message: response.data.message || 'Profile update failed'
      }
    } catch (error) {
      logger.error('❌ Update profile error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Profile update failed'
      }
    }
  },

  /**
   * Change password
   */
  async changePassword(passwordData) {
    try {
      const response = await api.put('/user/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      })

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        }
      }

      return {
        success: false,
        message: response.data.message || 'Password change failed'
      }
    } catch (error) {
      logger.error('❌ Change password error:', error)

      // Extract error message from nested error object
      let message = 'Password change failed'
      if (error.response?.data?.error?.message) {
        message = error.response.data.error.message
      } else if (error.response?.data?.message) {
        message = error.response.data.message
      } else if (error.message) {
        message = error.message
      }

      return {
        success: false,
        message,
        suggestions:
          error.response?.data?.error?.suggestions ||
          error.response?.data?.suggestions
      }
    }
  },

  /**
   * Request password reset
   */
  async forgotPassword(email) {
    try {
      const response = await api.post('/auth/forgot-password', { email })

      return {
        success: true,
        message: response.data.message || 'Password reset email sent'
      }
    } catch (error) {
      logger.error('❌ Forgot password error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send reset email'
      }
    }
  },

  /**
   * Reset password with token
   */
  async resetPassword(tokenData) {
    try {
      const response = await api.post('/auth/reset-password', {
        token: tokenData.token,
        newPassword: tokenData.newPassword,
        confirmPassword: tokenData.confirmPassword
      })

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        }
      }

      return {
        success: false,
        message: response.data.message || 'Password reset failed'
      }
    } catch (error) {
      logger.error('❌ Reset password error:', error)

      // Extract error message from nested error object
      let message = 'Password reset failed'
      if (error.response?.data?.error?.message) {
        message = error.response.data.error.message
      } else if (error.response?.data?.message) {
        message = error.response.data.message
      } else if (error.message) {
        message = error.message
      }

      return {
        success: false,
        message,
        suggestions:
          error.response?.data?.error?.suggestions ||
          error.response?.data?.suggestions
      }
    }
  },

  /**
   * Resend verification email
   */
  async resendVerification(email) {
    try {
      const response = await api.post('/auth/resend-verification', { email })

      return {
        success: response.data.success === true,
        message: response.data.message || 'Verification email sent'
      }
    } catch (error) {
      logger.error('❌ Resend verification error:', error)
      return {
        success: false,
        message:
          error.response?.data?.message || 'Failed to send verification email'
      }
    }
  },

  /**
   * Refresh access token
   */
  async refreshToken(attempt = authAttempts.capture()) {
    return refreshAuthToken(attempt)
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token) {
    try {
      const response = await api.post('/auth/verify-email', { token })

      return {
        success: response.data.success,
        message: response.data.message
      }
    } catch (error) {
      logger.error('❌ Email verification error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Email verification failed'
      }
    }
  },

  /**
   * Request password reset (alias for forgotPassword)
   */
  async requestPasswordReset(email) {
    return this.forgotPassword(email)
  }
}

/**
 * General API helper functions
 */
export const apiHelpers = {
  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!localStorage.getItem('user')
  },

  /**
   * Get stored user data
   */
  getStoredUser() {
    try {
      const user = localStorage.getItem('user')
      return user ? JSON.parse(user) : null
    } catch (error) {
      logger.error('Error parsing stored user:', error)
      localStorage.removeItem('user')
      return null
    }
  },

  /**
   * Check if user has specific role
   */
  hasRole(role) {
    const user = this.getStoredUser()
    return user?.role === role || false
  },

  /**
   * Check if user is verified
   */
  isVerified() {
    const user = this.getStoredUser()
    return user?.isVerified || false
  }
}

export const releaseCampaignService = {
  async getReleaseCampaign() {
    const response = await api.get('/config/release-campaign')
    return response.data?.data ?? response.data
  }
}

export default api
